/**
 * Dialogue modal (design system, Dialogue) : question, conséquence, deux boutons. Échap et
 * le clic sur le voile renoncent ; le focus reste dans le dialogue, puis revient au
 * déclencheur. Un contenu (champ de saisie) peut s'insérer entre le texte et les boutons.
 */
import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Bouton } from './Bouton';

export interface DialogueProps {
  titre: string;
  texte?: ReactNode;
  children?: ReactNode;
  actionSecondaire?: string;
  actionPrincipale: {
    libelle: string;
    variante?: 'principal' | 'danger';
    desactive?: boolean;
    /** Une promesse garde le dialogue ouvert, bouton occupé, jusqu'au résultat. */
    onAction: () => void | Promise<void>;
  };
  focusInitial?: 'principale' | 'secondaire' | 'contenu';
  onFermer: () => void;
}

export function Dialogue({
  titre,
  texte,
  children,
  actionSecondaire = 'Annuler',
  actionPrincipale,
  focusInitial,
  onFermer,
}: DialogueProps) {
  const id = useId();
  const ref = useRef<HTMLFormElement>(null);
  const secondaire = useRef<HTMLButtonElement>(null);
  const principale = useRef<HTMLButtonElement>(null);
  const declencheur = useRef(document.activeElement as HTMLElement | null);
  const [occupe, setOccupe] = useState(false);
  const danger = actionPrincipale.variante === 'danger';
  const focus = focusInitial ?? (children ? 'contenu' : danger ? 'secondaire' : 'principale');

  useEffect(() => {
    if (focus === 'secondaire') secondaire.current?.focus();
    else if (focus === 'principale') principale.current?.focus();
    else ref.current?.querySelector<HTMLElement>('input, textarea, select, button')?.focus();
    const retour = declencheur.current;
    return () => retour?.focus?.();
  }, [focus]);

  async function valider(e?: FormEvent) {
    e?.preventDefault();
    if (actionPrincipale.desactive || occupe) return;
    const resultat = actionPrincipale.onAction();
    if (resultat instanceof Promise) {
      setOccupe(true);
      try {
        await resultat;
      } finally {
        setOccupe(false);
      }
    }
  }

  return createPortal(
    <div
      className="fc-dialogue__voile"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !occupe) onFermer();
      }}
    >
      <form
        ref={ref}
        className="fc-dialogue"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-titre`}
        aria-describedby={texte ? `${id}-texte` : undefined}
        noValidate
        onSubmit={valider}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            if (!occupe) onFermer();
          } else if (e.key === 'Tab') {
            const focalisables = [
              ...(ref.current?.querySelectorAll<HTMLElement>(
                'input:not([disabled]), select, textarea, button:not([disabled]), [tabindex="0"]',
              ) ?? []),
            ];
            const premier = focalisables[0];
            const dernier = focalisables.at(-1);
            if (e.shiftKey && document.activeElement === premier) {
              e.preventDefault();
              dernier?.focus();
            } else if (!e.shiftKey && document.activeElement === dernier) {
              e.preventDefault();
              premier?.focus();
            }
          }
        }}
      >
        <h2 className="fc-dialogue__titre" id={`${id}-titre`}>
          {titre}
        </h2>
        {texte && (
          <p className="fc-dialogue__texte" id={`${id}-texte`}>
            {texte}
          </p>
        )}
        {children && <div className="fc-dialogue__contenu">{children}</div>}
        <div className="fc-dialogue__actions">
          <Bouton ref={secondaire} onClick={onFermer} desactive={occupe}>
            {actionSecondaire}
          </Bouton>
          <Bouton
            ref={principale}
            type="submit"
            variante={danger ? 'danger' : 'principal'}
            occupe={occupe}
            desactive={actionPrincipale.desactive ?? false}
          >
            {actionPrincipale.libelle}
          </Bouton>
        </div>
      </form>
    </div>,
    document.body,
  );
}
